import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { FileText, Plus, Upload, Calendar, Trash2, Eye, Loader2, AlertTriangle, Edit, X, Download } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const ToolboxManager = ({ token, isAdmin, isSupervisor }) => {
  const { toast } = useToast();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [topics, setTopics] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [newTopic, setNewTopic] = useState({
    title: '',
    content_text: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [editingTopic, setEditingTopic] = useState(null);
  const [selectedTopicId, setSelectedTopicId] = useState(null);

  useEffect(() => {
    if (token) {
      fetchTopics();
    }
  }, [token]);

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API}/toolbox/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTopics(response.data.topics || []);
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    }
  };

  const handleCreateTopic = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/toolbox/create`,
        newTopic,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Success',
        description: 'Toolbox topic created successfully',
      });

      setSelectedTopicId(response.data.id);
      setShowCreateDialog(false);
      setNewTopic({ title: '', content_text: '', date: new Date().toISOString().split('T')[0] });
      fetchTopics();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create topic',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditTopic = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.put(
        `${API}/toolbox/${editingTopic.id}`,
        {
          title: editingTopic.title,
          content_text: editingTopic.content_text,
          date: editingTopic.date
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Success',
        description: 'Toolbox topic updated successfully',
      });

      setShowEditDialog(false);
      setEditingTopic(null);
      fetchTopics();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update topic',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('Are you sure you want to delete this toolbox topic? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/toolbox/${topicId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: 'Success',
        description: 'Toolbox topic deleted successfully',
      });

      fetchTopics();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete topic',
        variant: 'destructive'
      });
    }
  };

  const handleRemovePdf = async (topicId) => {
    if (!window.confirm('Are you sure you want to remove the PDF from this topic?')) {
      return;
    }

    try {
      await axios.delete(`${API}/toolbox/${topicId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: 'Success',
        description: 'PDF removed successfully',
      });

      fetchTopics();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to remove PDF',
        variant: 'destructive'
      });
    }
  };

  const handleViewPdf = async (topic) => {
    try {
      // Show loading toast
      toast({
        title: 'Downloading PDF...',
        description: 'Please wait while we prepare your document',
      });

      const response = await axios.get(
        `${API}/toolbox/pdf/${topic.content_pdf_filename}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob' // Important for PDF binary data
        }
      );

      // Create a blob and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = topic.content_pdf_filename || 'toolbox-topic.pdf';
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Success',
        description: 'PDF downloaded successfully',
      });
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to download PDF file',
        variant: 'destructive'
      });
    }
  };

  const openEditDialog = (topic) => {
    setEditingTopic({
      id: topic.id,
      title: topic.title,
      content_text: topic.content_text || '',
      date: topic.date
    });
    setShowEditDialog(true);
  };

  const handlePdfUpload = async (topicId, file) => {
    if (!file) return;

    // Check file type
    if (file.type !== 'application/pdf') {
      toast({
        title: 'Invalid File',
        description: 'Only PDF files are allowed',
        variant: 'destructive'
      });
      return;
    }

    // Check file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'PDF must be under 10MB',
        variant: 'destructive'
      });
      return;
    }

    setUploadingPdf(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(
        `${API}/toolbox/${topicId}/upload-pdf`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast({
        title: 'Success',
        description: 'PDF uploaded successfully',
      });

      fetchTopics();
    } catch (error) {
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.detail || 'Failed to upload PDF',
        variant: 'destructive'
      });
    } finally {
      setUploadingPdf(false);
    }
  };

  if (!isAdmin && !isSupervisor) {
    return null;
  }

  return (
    <Card className="border-2">
      <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50 border-b">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <AlertTriangle className="w-6 h-6 text-orange-600" />
            <span>Toolbox Meeting Topics</span>
          </CardTitle>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Topic
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <p className="text-sm text-slate-400 mb-4">
          Create daily safety topics for your team. Users will see these when they clock in and confirm acknowledgment at clock-out.
        </p>

        {topics.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No toolbox topics yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {topics.map((topic) => (
              <Card key={topic.id} className="border">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="font-semibold text-white">{topic.title}</h4>
                        <Badge className="bg-blue-100 text-blue-800">
                          <Calendar className="w-3 h-3 mr-1" />
                          {topic.date}
                        </Badge>
                        {topic.active && (
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                        )}
                      </div>
                      {topic.content_text && (
                        <p className="text-sm text-slate-400 mb-2 line-clamp-2">{topic.content_text}</p>
                      )}
                      {topic.content_pdf_filename && (
                        <div className="flex items-center space-x-2 text-sm text-blue-600">
                          <FileText className="w-4 h-4" />
                          <span>PDF attached</span>
                        </div>
                      )}
                      <p className="text-xs text-slate-500 mt-2">
                        Created by {topic.created_by_name}
                      </p>
                    </div>
                    <div className="flex flex-col space-y-2">
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => openEditDialog(topic)}
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteTopic(topic.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      {!topic.content_pdf_filename ? (
                        <>
                          <input
                            type="file"
                            id={`pdf-upload-${topic.id}`}
                            accept="application/pdf"
                            onChange={(e) => handlePdfUpload(topic.id, e.target.files[0])}
                            className="hidden"
                          />
                          <label htmlFor={`pdf-upload-${topic.id}`}>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={uploadingPdf}
                              onClick={(e) => {
                                e.preventDefault();
                                document.getElementById(`pdf-upload-${topic.id}`).click();
                              }}
                              className="w-full"
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              Upload PDF
                            </Button>
                          </label>
                        </>
                      ) : (
                        <>
                          <Button
                            onClick={() => handleViewPdf(topic)}
                            variant="outline"
                            size="sm"
                            className="w-full"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download PDF
                          </Button>
                          <Button
                            onClick={() => handleRemovePdf(topic.id)}
                            variant="outline"
                            size="sm"
                            className="w-full text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Remove PDF
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Topic Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
                <span>Create Toolbox Topic</span>
              </DialogTitle>
              <DialogDescription>
                Create a daily safety topic for your team
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleCreateTopic} className="space-y-4">
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={newTopic.date}
                  onChange={(e) => setNewTopic({ ...newTopic, date: e.target.value })}
                  className="h-11"
                  required
                />
              </div>

              <div>
                <Label htmlFor="title">Topic Title</Label>
                <Input
                  id="title"
                  placeholder="e.g., Ladder Safety, Fire Extinguisher Use"
                  value={newTopic.title}
                  onChange={(e) => setNewTopic({ ...newTopic, title: e.target.value })}
                  className="h-11"
                  required
                />
              </div>

              <div>
                <Label htmlFor="content">Content (Optional - can upload PDF after)</Label>
                <Textarea
                  id="content"
                  placeholder="Enter safety information, reminders, or procedures..."
                  value={newTopic.content_text}
                  onChange={(e) => setNewTopic({ ...newTopic, content_text: e.target.value })}
                  rows={5}
                />
                <p className="text-xs text-slate-500 mt-1">
                  You can add a PDF attachment after creating the topic
                </p>
              </div>

              <div className="flex space-x-3">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Create Topic
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowCreateDialog(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Topic Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <Edit className="w-6 h-6 text-blue-600" />
                <span>Edit Toolbox Topic</span>
              </DialogTitle>
              <DialogDescription>
                Update the toolbox topic details
              </DialogDescription>
            </DialogHeader>

            {editingTopic && (
              <form onSubmit={handleEditTopic} className="space-y-4">
                <div>
                  <Label htmlFor="edit-date">Date</Label>
                  <Input
                    id="edit-date"
                    type="date"
                    value={editingTopic.date}
                    onChange={(e) => setEditingTopic({ ...editingTopic, date: e.target.value })}
                    className="h-11"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="edit-title">Topic Title</Label>
                  <Input
                    id="edit-title"
                    placeholder="e.g., Ladder Safety, Fire Extinguisher Use"
                    value={editingTopic.title}
                    onChange={(e) => setEditingTopic({ ...editingTopic, title: e.target.value })}
                    className="h-11"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="edit-content">Content</Label>
                  <Textarea
                    id="edit-content"
                    placeholder="Enter safety information, reminders, or procedures..."
                    value={editingTopic.content_text}
                    onChange={(e) => setEditingTopic({ ...editingTopic, content_text: e.target.value })}
                    rows={5}
                  />
                </div>

                <div className="flex space-x-3">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Edit className="w-4 h-4 mr-2" />
                    )}
                    Update Topic
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setShowEditDialog(false);
                      setEditingTopic(null);
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ToolboxManager;
